// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import { IonGrid, IonRow } from '@ionic/react'
import { useSelector } from 'react-redux'
import {
	CollectionItem,
	ScreenSection,
	MapContainer as Container,
	Collection,
	RootStoreState,
} from 'mytours-read-common'
import { GetCollectionsShowResponseMapLayers, MapRoute } from 'mytours-read-api-typescript'
import classnames from 'classnames'

import { getCurrentLanguage } from '../../platform'
import {
	GeoreferencedLayerType,
	LayerTypeEnum,
	MapboxLayerType,
	MapContainer,
	RouteProfile,
	StaticMapLayerType,
} from '../types'

import MapboxMap from './MapboxMap'
import LeafletMap from './LeafletMap'
import InAppDirections from './InAppDirections'
import ImagePlaceholder from 'mobile_web/modules/common/components/placeholders/Image'

import 'swiper/swiper-bundle.min.css'
import 'swiper/swiper.min.css'
import '../styles.less'

interface OwnProps extends MapContainer {
	accountId?: number
	renderMapControls: () => JSX.Element
	selectedMapLayerId?: number
	activeItemIndex?: number
	onSetActiveItem: (activeItem: CollectionItem | ScreenSection, activeItemIndex: number) => void
	onMapReady?: () => void
	mapLayers: GetCollectionsShowResponseMapLayers[]
	mapType: MapboxLayerType | StaticMapLayerType | GeoreferencedLayerType | undefined
	onMapResize?(): void
	itemType?: MapRoute.LinkedTypeEnum
}
type Props = Container.Props & Container.Actions & OwnProps

const Map: React.FC<Props> = props => {
	const [loading, setLoading] = useState<boolean>(true)
	const [showInAppDirections, setShowInAppDirections] = useState<boolean>(false)
	const loadingItems = useSelector(
		(state: RootStoreState) => state.collectionlist.collectionItems?.get(props?.details?.id)?.loading,
	)

	const isItemsLoaded = loadingItems !== undefined && !loadingItems

	useEffect(() => {
		if (props.details) {
			props.loadMapRoutes(props.details, props.itemType, getCurrentLanguage(), props.accountId)
		}
	}, [])

	/**
	 * Only handles mapbox (online) maps currently. Assumes there is only 1 layer.
	 */
	const extractMapboxMapStyle = (): string | undefined => {
		for (const layer of props.mapLayers) {
			// check if mapbox style (not static)
			if (layer.layer_type === LayerTypeEnum.Mapbox) {
				// Static
				return layer.style_id
			}
		}

		return
	}

	const renderMap = (): JSX.Element => {
		const {
			mapRoutes,
			items,
			currentItemIndex,
			boundAll,
			renderMapControls,
			selectedMapLayerId,
			activeItemIndex,
			containerType,
			containerId,
			onMapReady,
			mapType,
			onMapResize,
			details,
		} = props

		if (loading) {
			return <ImagePlaceholder style={{ width: '100%', height: '100' }} />
		}

		switch (mapType) {
			case LayerTypeEnum.Mapbox: {
				return (
					<MapboxMap
						items={items}
						mapControls={renderMapControls}
						currentItemIndex={activeItemIndex ?? currentItemIndex}
						extractMapboxMapStyle={extractMapboxMapStyle}
						boundAll={boundAll}
						onSetActiveItem={props.onSetActiveItem}
						mapRoutes={mapRoutes}
						showInAppDirections={setShowInAppDirections}
						containerType={containerType}
						containerId={containerId}
						onMapReady={onMapReady}
						isItemsLoaded={isItemsLoaded}
						onMapResize={onMapResize}
						mediaItems={props.mediaItems}
						loadMediaItem={props.loadMediaItem}
						elevationEnabled={details && 'map' in details ? details.map.elevation_enabled : false}
					/>
				)
			}
			case LayerTypeEnum.Static:
			case LayerTypeEnum.Georeferenced: {
				const { details } = props
				const { map } = details as Collection

				return (
					<LeafletMap
						items={items}
						mapControls={renderMapControls}
						currentItemIndex={activeItemIndex ?? currentItemIndex}
						selectedMapLayerId={selectedMapLayerId}
						currentMap={map}
						onSetActiveItem={props.onSetActiveItem}
						showInAppDirections={setShowInAppDirections}
						containerType={containerType}
						containerId={containerId}
						onMapReady={onMapReady}
						mapType={mapType}
						onMapResize={onMapResize}
					/>
				)
			}
			default:
				return
		}
	}

	return (
		<>
			<IonGrid
				className={classnames('map-grid', props.activeItemIndex >= 0 && 'has-active-item')}
				ref={ref => {
					if (ref && loading) {
						setTimeout(() => setLoading(false))
					}
				}}
			>
				<IonRow className="map-row">{renderMap()}</IonRow>
			</IonGrid>
			<InAppDirections
				showModal={showInAppDirections}
				activeItem={props.items[props.activeItemIndex]}
				toggleModal={setShowInAppDirections}
				profileType={(props.details as Collection)?.tour_type as RouteProfile}
				containerType={props.containerType}
				containerId={props.containerId}
			/>
		</>
	)
}

const MapComponent = Container.default(Map)
export default MapComponent as any
